import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
  Grid
} from 'antd';
import ColumnsCardList from '../common/ColumnsCardList';
import { EditOutlined, PlusOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { deliveryPersonsApi, productsApi } from '../../utils/api';

const { Text } = Typography;

/**
 * Configuration générique des mouvements de colis.
 * Chaque type décrit son libellé, son champ date et ses appels API :
 * ajouter un nouveau type de mouvement ne demande qu'une entrée ici.
 */
const MOVEMENT_TYPES = {
  handover: {
    label: 'Remise au livreur',
    dateField: 'handed_over_at',
    create: deliveryPersonsApi.createParcelHandover,
    update: deliveryPersonsApi.updateParcelHandover,
    successMessage: 'Remise enregistrée : le solde du livreur a été mis à jour'
  },
  return: {
    label: 'Retour en boutique',
    dateField: 'returned_at',
    create: deliveryPersonsApi.createParcelReturn,
    update: deliveryPersonsApi.updateParcelReturn,
    successMessage: 'Retour enregistré : le solde du livreur a été mis à jour'
  }
};

// Interdit la sélection d'une date future pour les mouvements de colis.
const disableFutureDates = (current) => current && current.endOf('day').isAfter(dayjs().endOf('day'));

// Affiche un article et sa variante de façon homogène dans tous les tableaux.
const renderVariant = (_, record) => (
  <Space direction="vertical" size={0}>
    <Text strong>{record.product_name}</Text>
    <Text type="secondary" className="text-xs">
      {record.variant_name}{record.sku ? ` · ${record.sku}` : ''}
    </Text>
  </Space>
);

// Formate une date au format jour/mois/année.
const formatDate = (date) => (date ? dayjs(date).format('DD/MM/YYYY') : '—');

/**
 * Section dédiée au suivi des colis d'un livreur :
 * colis récupérés (remis), livrés, retournés, en main et besoins pour une date de livraison.
 * @param {Array} deliveryPersons - Liste des livreurs disponibles.
 * @param {Function} getStatusLabel - Retourne le libellé d'un statut de commande.
 * @param {Function} getStatusColor - Retourne la couleur d'un statut de commande.
 */
const DeliveryPersonParcels = ({ deliveryPersons = [], getStatusLabel = (s) => s, getStatusColor = () => 'default' }) => {
  // Mobile (< md) : cartes à la place des tableaux d'historique ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [needsDate, setNeedsDate] = useState(dayjs());
  const [parcelData, setParcelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);

  // Formulaire générique d'ajout d'un mouvement (remise ou retour).
  const [movementType, setMovementType] = useState('handover');
  const [movementVariantId, setMovementVariantId] = useState(null);
  const [movementQuantity, setMovementQuantity] = useState(1);
  const [movementDate, setMovementDate] = useState(dayjs());
  const [savingMovement, setSavingMovement] = useState(false);

  // Modale générique de correction : { type, record, quantity, date }.
  const [movementToEdit, setMovementToEdit] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Charge le catalogue une seule fois pour alimenter le sélecteur de variantes.
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await productsApi.getAll();
        setProducts(response?.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des articles pour les colis:', error);
        message.error('Impossible de charger les articles pour le suivi des colis');
      }
    };
    loadProducts();
  }, []);

  /**
   * Charge bilan, historiques et besoins du livreur pour la date choisie.
   * @param {string|null} personId - Livreur ciblé.
   * @param {dayjs.Dayjs|null} date - Date de livraison des commandes à préparer.
   */
  const loadParcelData = async (personId = selectedPersonId, date = needsDate) => {
    if (!personId) {
      setParcelData(null);
      return;
    }

    try {
      setLoading(true);
      const response = await deliveryPersonsApi.getParcelSummary(personId, date ? date.format('YYYY-MM-DD') : null);
      setParcelData(response?.data || null);
    } catch (error) {
      console.error('Erreur lors du chargement du suivi colis:', error);
      message.error('Impossible de charger le suivi des colis de ce livreur');
      setParcelData(null);
    } finally {
      setLoading(false);
    }
  };

  // Changement de livreur : réinitialise le formulaire et recharge les données.
  const handlePersonChange = (personId) => {
    setSelectedPersonId(personId);
    setMovementVariantId(null);
    loadParcelData(personId, needsDate);
  };

  // Changement de date des besoins : recharge uniquement les données du livreur courant.
  const handleNeedsDateChange = (date) => {
    setNeedsDate(date);
    loadParcelData(selectedPersonId, date);
  };

  const summary = parcelData?.summary || [];
  const needs = parcelData?.needs?.needs || [];

  // Totaux affichés en tête de section.
  const totals = useMemo(() => {
    const sum = (list, field) => list.reduce((total, line) => total + Number(line[field] || 0), 0);
    return {
      handedOver: sum(summary, 'handed_over_quantity'),
      delivered: sum(summary, 'delivered_quantity'),
      returned: sum(summary, 'returned_quantity'),
      remaining: sum(summary, 'remaining_quantity'),
      required: sum(needs, 'required_quantity'),
      toHandOver: sum(needs, 'to_hand_over_quantity')
    };
  }, [summary, needs]);

  // Options de variantes : tout le catalogue actif pour une remise,
  // uniquement les variantes encore en main pour un retour.
  const variantOptions = useMemo(() => {
    if (movementType === 'return') {
      return summary
        .filter((line) => line.remaining_quantity > 0)
        .map((line) => ({
          value: line.product_variant_id,
          label: `${line.product_name} — ${line.variant_name} (en main : ${line.remaining_quantity})`
        }));
    }

    return products.flatMap((product) =>
      (product.product_variants || [])
        .filter((variant) => variant.status !== 'inactive')
        .map((variant) => ({
          value: variant.id,
          label: `${product.name} — ${variant.title || 'Variante par défaut'}${variant.sku ? ` (${variant.sku})` : ''}`
        }))
    );
  }, [movementType, products, summary]);

  /**
   * Pré-remplit le formulaire (ex. bouton « Remettre » d'une ligne de besoin).
   * @param {string} type - Clé de MOVEMENT_TYPES.
   * @param {string} variantId - Variante concernée.
   * @param {number} quantity - Quantité proposée.
   */
  const prefillMovement = (type, variantId, quantity) => {
    setMovementType(type);
    setMovementVariantId(variantId);
    setMovementQuantity(Math.max(1, quantity));
    setMovementDate(dayjs());
  };

  // Enregistre une remise ou un retour selon le type sélectionné.
  const handleMovementSubmit = async () => {
    if (!selectedPersonId || !movementVariantId || !Number.isInteger(movementQuantity) || movementQuantity <= 0 || !movementDate) {
      message.warning('Sélectionnez une variante, une date et une quantité entière supérieure à zéro');
      return;
    }

    const config = MOVEMENT_TYPES[movementType];
    try {
      setSavingMovement(true);
      await config.create(selectedPersonId, {
        product_variant_id: movementVariantId,
        quantity: movementQuantity,
        [config.dateField]: movementDate.format('YYYY-MM-DD')
      });
      message.success(config.successMessage);
      setMovementVariantId(null);
      setMovementQuantity(1);
      setMovementDate(dayjs());
      await loadParcelData();
    } catch (error) {
      console.error('Erreur lors de l’enregistrement du mouvement de colis:', error);
      message.error(error.response?.data?.error || 'Impossible d’enregistrer ce mouvement');
    } finally {
      setSavingMovement(false);
    }
  };

  // Enregistre la correction d'une remise ou d'un retour.
  const handleEditSave = async () => {
    const { type, record, quantity, date } = movementToEdit || {};
    if (!record || !date || !Number.isInteger(quantity) || quantity <= 0) {
      message.warning('Saisissez une date et une quantité valides');
      return;
    }

    const config = MOVEMENT_TYPES[type];
    try {
      setSavingEdit(true);
      await config.update(selectedPersonId, record.id, {
        quantity,
        [config.dateField]: date.format('YYYY-MM-DD')
      });
      message.success('Correction enregistrée');
      setMovementToEdit(null);
      await loadParcelData();
    } catch (error) {
      console.error('Erreur lors de la correction du mouvement de colis:', error);
      message.error(error.response?.data?.error || 'Impossible d’enregistrer la correction');
    } finally {
      setSavingEdit(false);
    }
  };

  // Ouvre la modale de correction avec les valeurs actuelles du mouvement.
  const openEdit = (type, record) => setMovementToEdit({
    type,
    record,
    quantity: record.quantity,
    date: dayjs(record[MOVEMENT_TYPES[type].dateField])
  });

  // Bouton de correction réutilisé par les historiques de remises et de retours.
  const editColumn = (type) => ({
    title: 'Action',
    key: 'action',
    width: 100,
    render: (_, record) => (
      <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(type, record)}>
        Modifier
      </Button>
    )
  });

  const summaryColumns = [
    { title: 'Article / variante', key: 'variant', render: renderVariant },
    { title: 'Récupérés', dataIndex: 'handed_over_quantity', align: 'center', width: 95 },
    { title: 'Livrés', dataIndex: 'delivered_quantity', align: 'center', width: 80 },
    { title: 'Retournés', dataIndex: 'returned_quantity', align: 'center', width: 95 },
    {
      title: 'En main',
      dataIndex: 'remaining_quantity',
      align: 'center',
      width: 90,
      render: (quantity) => <Tag color={quantity > 0 ? 'gold' : 'success'}>{quantity}</Tag>
    },
    { title: 'Dernière remise', dataIndex: 'last_handover_at', width: 130, responsive: ['md'], render: formatDate }
  ];

  const needsColumns = [
    { title: 'Article / variante', key: 'variant', render: renderVariant },
    {
      title: 'Commandes',
      dataIndex: 'order_numbers',
      responsive: ['md'],
      render: (numbers) => (numbers || []).map((number) => <Tag key={number}>#{number}</Tag>)
    },
    { title: 'Besoin', dataIndex: 'required_quantity', align: 'center', width: 80 },
    { title: 'En main', dataIndex: 'in_hand_quantity', align: 'center', width: 85 },
    {
      title: 'À remettre',
      dataIndex: 'to_hand_over_quantity',
      align: 'center',
      width: 100,
      render: (quantity) => <Tag color={quantity > 0 ? 'red' : 'success'}>{quantity}</Tag>
    },
    {
      title: 'Action',
      key: 'action',
      width: 110,
      render: (_, record) => record.to_hand_over_quantity > 0 && (
        <Button
          type="link"
          icon={<PlusOutlined />}
          onClick={() => prefillMovement('handover', record.product_variant_id, record.to_hand_over_quantity)}
        >
          Remettre
        </Button>
      )
    }
  ];

  const ordersColumns = [
    {
      title: 'N°',
      dataIndex: 'order_number',
      width: 90,
      render: (number, record) => <a href={`/app/orders/${record.id}`} target="_blank" rel="noreferrer">#{number}</a>
    },
    {
      title: 'Client',
      key: 'customer',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.customer_name || '—'}</Text>
          {record.customer_phone && <Text type="secondary" className="text-xs">{record.customer_phone}</Text>}
        </Space>
      )
    },
    { title: 'Adresse', dataIndex: 'delivery_address', ellipsis: true, responsive: ['md'] },
    { title: 'Articles', dataIndex: 'items_quantity', align: 'center', width: 80 },
    {
      title: 'Statut',
      dataIndex: 'status',
      width: 120,
      render: (status) => <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
    }
  ];

  const handoverColumns = [
    { title: 'Article / variante', key: 'variant', render: renderVariant },
    { title: 'Date', dataIndex: 'handed_over_at', width: 110, render: formatDate },
    { title: 'Remis', dataIndex: 'quantity', align: 'center', width: 75 },
    { title: 'Livrés', dataIndex: 'delivered_quantity', align: 'center', width: 75 },
    editColumn('handover')
  ];

  const deliveriesColumns = [
    {
      title: 'Commande',
      dataIndex: 'order_number',
      width: 110,
      render: (number, record) => (record.order_id
        ? <a href={`/app/orders/${record.order_id}`} target="_blank" rel="noreferrer">#{number}</a>
        : '—')
    },
    { title: 'Article / variante', key: 'variant', render: renderVariant },
    { title: 'Qté', dataIndex: 'quantity', align: 'center', width: 70 },
    { title: 'Livré le', dataIndex: 'delivered_at', width: 110, render: formatDate }
  ];

  const returnsColumns = [
    { title: 'Article / variante', key: 'variant', render: renderVariant },
    { title: 'Date', dataIndex: 'returned_at', width: 110, render: formatDate },
    { title: 'Retournés', dataIndex: 'quantity', align: 'center', width: 90 },
    { title: 'Note', dataIndex: 'notes', ellipsis: true, responsive: ['md'] },
    editColumn('return')
  ];

  // Tableau d'historique générique (pagination et message vide communs).
  const renderTable = (columns, rows, rowKey, emptyText) => isMobile ? (
    <ColumnsCardList
      columns={columns}
      dataSource={rows}
      rowKey={rowKey}
      loading={loading}
      actionKeys={['action']}
      clientPageSize={10}
      emptyText={emptyText}
    />
  ) : (
    <Table
      columns={columns}
      dataSource={rows}
      rowKey={rowKey}
      loading={loading}
      size="small"
      pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
      scroll={{ x: 'max-content' }}
      locale={{ emptyText }}
    />
  );

  const editConfig = movementToEdit ? MOVEMENT_TYPES[movementToEdit.type] : null;
  // Une remise ne peut pas descendre sous les colis déjà livrés depuis celle-ci.
  const editMinQuantity = movementToEdit?.type === 'handover'
    ? Math.max(1, movementToEdit.record.delivered_quantity || 0)
    : 1;

  return (
    <Card className="p-3 sm:p-6">
      {/* Filtres : livreur et date des commandes à préparer */}
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_200px_auto] gap-4 items-end mb-4">
        <div>
          <Text strong className="text-xs sm:text-sm">Livreur :</Text>
          <Select
            className="w-full mt-2"
            placeholder="Choisir un livreur"
            value={selectedPersonId}
            onChange={handlePersonChange}
            allowClear
            showSearch
            optionFilterProp="label"
            options={deliveryPersons
              .filter((person) => person.is_active)
              .map((person) => ({ value: person.id, label: person.name }))}
            suffixIcon={<UserOutlined />}
          />
        </div>
        <div>
          <Text strong className="text-xs sm:text-sm">Commandes du :</Text>
          <DatePicker
            className="w-full mt-2"
            value={needsDate}
            onChange={handleNeedsDateChange}
            format="DD/MM/YYYY"
            allowClear={false}
          />
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => loadParcelData()} disabled={!selectedPersonId}>
          Actualiser
        </Button>
      </div>

      {!selectedPersonId ? (
        <Empty description="Sélectionnez un livreur pour voir ses colis" className="py-12" />
      ) : (
        <>
          {/* Indicateurs clés du livreur */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <Card size="small"><Statistic title="Récupérés" value={totals.handedOver} /></Card>
            <Card size="small"><Statistic title="Livrés" value={totals.delivered} valueStyle={{ color: '#52c41a' }} /></Card>
            <Card size="small"><Statistic title="Retournés" value={totals.returned} valueStyle={{ color: '#8c8c8c' }} /></Card>
            <Card size="small"><Statistic title="En main" value={totals.remaining} valueStyle={{ color: '#faad14' }} /></Card>
            <Card size="small"><Statistic title={`Besoin ${needsDate.format('DD/MM')}`} value={totals.required} /></Card>
            <Card size="small">
              <Statistic
                title="À remettre"
                value={totals.toHandOver}
                valueStyle={{ color: totals.toHandOver > 0 ? '#ff4d4f' : '#52c41a' }}
              />
            </Card>
          </div>

          {/* Formulaire générique : remise au livreur ou retour en boutique */}
          <Card size="small" className="mb-4" title="Enregistrer un mouvement de colis">
            <Radio.Group
              className="mb-3"
              value={movementType}
              onChange={(event) => { setMovementType(event.target.value); setMovementVariantId(null); }}
              optionType="button"
              buttonStyle="solid"
              options={Object.entries(MOVEMENT_TYPES).map(([value, config]) => ({ value, label: config.label }))}
            />
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_120px_160px_auto] gap-3 items-end">
              <div>
                <Text strong className="text-xs sm:text-sm">Article et variante</Text>
                <Select
                  className="w-full mt-2"
                  placeholder="Sélectionner une variante"
                  showSearch
                  optionFilterProp="label"
                  options={variantOptions}
                  value={movementVariantId}
                  onChange={setMovementVariantId}
                  notFoundContent={movementType === 'return' ? 'Aucun colis en main' : 'Aucune variante disponible'}
                />
              </div>
              <div>
                <Text strong className="text-xs sm:text-sm">Quantité</Text>
                <Input
                  className="w-full mt-2"
                  type="number"
                  min="1"
                  step="1"
                  value={movementQuantity}
                  onChange={(event) => setMovementQuantity(Number(event.target.value))}
                  aria-label="Quantité du mouvement"
                />
              </div>
              <div>
                <Text strong className="text-xs sm:text-sm">Date</Text>
                <DatePicker
                  className="w-full mt-2"
                  value={movementDate}
                  format="DD/MM/YYYY"
                  allowClear={false}
                  disabledDate={disableFutureDates}
                  onChange={setMovementDate}
                  aria-label="Date du mouvement"
                />
              </div>
              <Button type="primary" icon={<PlusOutlined />} loading={savingMovement} onClick={handleMovementSubmit}>
                Enregistrer
              </Button>
            </div>
          </Card>

          {/* Détails : besoins du jour, bilan et historiques */}
          <Tabs
            defaultActiveKey="needs"
            items={[
              {
                key: 'needs',
                label: `Besoins du ${needsDate.format('DD/MM')}`,
                children: (
                  <Space direction="vertical" size="middle" className="w-full">
                    {(parcelData?.needs?.items_without_variant || []).length > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message="Certains articles n'ont pas de variante et ne peuvent pas être suivis"
                        description={parcelData.needs.items_without_variant
                          .map((item) => `#${item.order_number} : ${item.product_name} × ${item.quantity}`)
                          .join(' · ')}
                      />
                    )}
                    {renderTable(needsColumns, needs, 'product_variant_id', 'Aucun colis nécessaire pour cette date')}
                    <Text strong>Commandes attribuées ({parcelData?.needs?.orders?.length || 0})</Text>
                    {renderTable(ordersColumns, parcelData?.needs?.orders || [], 'id', 'Aucune commande à livrer pour cette date')}
                  </Space>
                )
              },
              {
                key: 'summary',
                label: 'Bilan par article',
                children: renderTable(summaryColumns, summary, 'product_variant_id', 'Aucun colis encore remis à ce livreur')
              },
              {
                key: 'handovers',
                label: `Remises (${parcelData?.handovers?.length || 0})`,
                children: renderTable(handoverColumns, parcelData?.handovers || [], 'id', 'Aucune remise enregistrée')
              },
              {
                key: 'deliveries',
                label: `Livraisons (${parcelData?.deliveries?.length || 0})`,
                children: renderTable(
                  deliveriesColumns,
                  parcelData?.deliveries || [],
                  (record) => `${record.order_id}-${record.product_name}-${record.variant_name}-${record.delivered_at}`,
                  'Aucune livraison déduite'
                )
              },
              {
                key: 'returns',
                label: `Retours (${parcelData?.returns?.length || 0})`,
                children: renderTable(returnsColumns, parcelData?.returns || [], 'id', 'Aucun retour enregistré')
              }
            ]}
          />
        </>
      )}

      {/* Modale générique de correction d'une remise ou d'un retour */}
      <Modal
        title={editConfig ? `Corriger : ${editConfig.label}` : ''}
        open={Boolean(movementToEdit)}
        onCancel={() => setMovementToEdit(null)}
        onOk={handleEditSave}
        okText="Enregistrer"
        cancelText="Annuler"
        confirmLoading={savingEdit}
      >
        {movementToEdit && (
          <Space direction="vertical" size="middle" className="w-full">
            <Text>{movementToEdit.record.product_name} — {movementToEdit.record.variant_name}</Text>
            <div>
              <Text strong>Quantité</Text>
              <Input
                className="w-full mt-2"
                type="number"
                min={editMinQuantity}
                step="1"
                value={movementToEdit.quantity}
                onChange={(event) => setMovementToEdit({ ...movementToEdit, quantity: Number(event.target.value) })}
                aria-label="Nouvelle quantité"
              />
              <Text type="secondary" className="text-xs">Minimum : {editMinQuantity}.</Text>
            </div>
            <div>
              <Text strong>Date</Text>
              <DatePicker
                className="w-full mt-2"
                value={movementToEdit.date}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={disableFutureDates}
                onChange={(date) => setMovementToEdit({ ...movementToEdit, date })}
                aria-label="Nouvelle date"
              />
            </div>
          </Space>
        )}
      </Modal>
    </Card>
  );
};

export default DeliveryPersonParcels;
